import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  initOfflineDb,
  saveToOfflineStore,
  getFromOfflineStore,
  getPendingSyncItems,
  updateSyncItemStatus,
  removeSyncItem,
  addToSyncQueue,
  SyncQueueItem
} from '@/lib/offlineDb';

export const useOfflineSync = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const { toast } = useToast();
  const syncIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initialize offline database
  useEffect(() => {
    initOfflineDb().catch(console.error);
  }, []);

  // Network status detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast({
        title: 'Back Online',
        description: 'Syncing pending changes...',
      });
      syncPendingChanges();
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast({
        title: 'Offline Mode',
        description: 'Changes will be saved locally and synced when back online.',
        variant: 'destructive',
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [toast]);

  // Update pending count periodically
  useEffect(() => {
    const updatePendingCount = async () => {
      const items = await getPendingSyncItems();
      setPendingCount(items.length);
    };

    updatePendingCount();
    const interval = setInterval(updatePendingCount, 5000);
    return () => clearInterval(interval);
  }, []);

  // Cache data from server
  const cacheData = useCallback(async (table: string, data: any[]) => {
    try {
      await saveToOfflineStore(table as any, data);
      console.log(`Cached ${data.length} items to ${table}`);
    } catch (error) {
      console.error(`Failed to cache ${table}:`, error);
    }
  }, []);

  // Get cached data
  const getCachedData = useCallback(async <T>(table: string): Promise<T[]> => {
    try {
      return await getFromOfflineStore<T>(table as any);
    } catch (error) {
      console.error(`Failed to get cached ${table}:`, error);
      return [];
    }
  }, []);

  // Queue offline mutation
  const queueOfflineMutation = useCallback(async (
    table: string,
    operation: 'insert' | 'update' | 'delete',
    data: any
  ): Promise<string> => {
    const id = await addToSyncQueue({ table, operation, data });
    
    // Update local cache immediately for optimistic UI
    if (operation === 'insert' || operation === 'update') {
      const cached = await getFromOfflineStore<any>(table as any);
      const existing = cached.findIndex(item => item.id === data.id);
      
      if (existing >= 0) {
        cached[existing] = { ...cached[existing], ...data };
      } else {
        cached.push(data);
      }
      
      await saveToOfflineStore(table as any, cached);
    }
    
    const items = await getPendingSyncItems();
    setPendingCount(items.length);
    
    return id;
  }, []);

  // Sync a single item
  const syncItem = async (item: SyncQueueItem): Promise<boolean> => {
    try {
      await updateSyncItemStatus(item.id, 'syncing');

      let result;
      switch (item.operation) {
        case 'insert':
          result = await supabase.from(item.table as any).insert(item.data);
          break;
        case 'update':
          result = await supabase.from(item.table as any).update(item.data).eq('id', item.data.id);
          break;
        case 'delete':
          result = await supabase.from(item.table as any).delete().eq('id', item.data.id);
          break;
      }

      if (result?.error) {
        throw result.error;
      }

      await removeSyncItem(item.id);
      return true;
    } catch (error: any) {
      console.error(`Sync failed for ${item.table}:`, error);
      
      // If max retries reached, mark as failed permanently
      if (item.retries >= 3) {
        await updateSyncItemStatus(item.id, 'failed', error.message);
        return false;
      }
      
      await updateSyncItemStatus(item.id, 'failed', error.message);
      return false;
    }
  };

  // Sync all pending changes
  const syncPendingChanges = useCallback(async () => {
    if (!isOnline || isSyncing) return;

    setIsSyncing(true);
    
    try {
      const pendingItems = await getPendingSyncItems();
      let successCount = 0;
      let failCount = 0;

      for (const item of pendingItems) {
        const success = await syncItem(item);
        if (success) {
          successCount++;
        } else {
          failCount++;
        }
      }

      setLastSyncTime(new Date());
      
      const remainingItems = await getPendingSyncItems();
      setPendingCount(remainingItems.length);

      if (successCount > 0) {
        toast({
          title: 'Sync Complete',
          description: `${successCount} changes synced successfully${failCount > 0 ? `, ${failCount} failed` : ''}.`,
        });
      }
    } catch (error) {
      console.error('Sync error:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [isOnline, isSyncing, toast]);

  // Auto-sync when online
  useEffect(() => {
    if (isOnline && pendingCount > 0 && !isSyncing) {
      // Debounce sync attempts
      if (syncIntervalRef.current) {
        clearTimeout(syncIntervalRef.current);
      }
      
      syncIntervalRef.current = setTimeout(() => {
        syncPendingChanges();
      }, 2000);
    }

    return () => {
      if (syncIntervalRef.current) {
        clearTimeout(syncIntervalRef.current);
      }
    };
  }, [isOnline, pendingCount, isSyncing, syncPendingChanges]);

  return {
    isOnline,
    isSyncing,
    pendingCount,
    lastSyncTime,
    cacheData,
    getCachedData,
    queueOfflineMutation,
    syncPendingChanges,
  };
};
